import { Accordion, Alert, Anchor, Badge, Group, List, Stack, Text, ThemeIcon, Title, Tooltip } from "@mantine/core"

const PRIMARY_QUESTION_FIELDS = new Set(["title", "description", "resolution_criteria", "fine_print", "options"])
const HIDDEN_QUESTION_FIELDS = new Set(["id", "created_at", "details_redacted", "external_source"])

const ADDITIONAL_QUESTION_FIELD_ORDER = {
  multiple_choice: ["options_are_comprehensive", "options_are_mutually_exclusive"],
  numeric: ["lower_bound", "upper_bound", "scale_type", "open_lower_bound", "open_upper_bound"],
  discrete: ["nominal_min", "nominal_max", "inbound_outcome_count", "open_lower_bound", "open_upper_bound"],
}

const QUESTION_FIELD_DEFINITIONS = {
  options_are_comprehensive: {
    label: "Options cover all outcomes",
    helpText:
      'Yes means the listed options fully cover every possible resolution, usually because there is an "Other" option when needed.',
    formatValue: formatBooleanValue,
  },
  options_are_mutually_exclusive: {
    label: "Options are mutually exclusive",
    helpText:
      "Yes means only one option can resolve as correct. No means more than one option could be true at the same time.",
    formatValue: formatBooleanValue,
  },
  nominal_min: {
    label: "Nominal minimum",
    helpText: "The lowest in-range whole number the forecast is designed to cover.",
  },
  nominal_max: {
    label: "Nominal maximum",
    helpText: "The highest in-range whole number the forecast is designed to cover.",
  },
  inbound_outcome_count: {
    label: "In-range outcome count",
    helpText: "The number of in-range outcomes between the nominal minimum and nominal maximum, inclusive.",
  },
  lower_bound: {
    label: "Lower bound",
    helpText: "The lowest in-range numeric value the forecast grid is designed to cover.",
  },
  upper_bound: {
    label: "Upper bound",
    helpText: "The highest in-range numeric value the forecast grid is designed to cover.",
  },
  scale_type: {
    label: "Scale type",
    helpText: 'Linear uses evenly spaced values. Logarithmic uses multiplicative spacing and requires positive bounds.',
    formatValue: (value) => (value === "logarithmic" ? "Logarithmic" : "Linear"),
  },
  open_lower_bound: {
    label: "Open lower bound",
    helpText:
      "Yes means outcomes below the lower bound or nominal minimum are still possible. No means lower outcomes are impossible by definition.",
    formatValue: formatBooleanValue,
  },
  open_upper_bound: {
    label: "Open upper bound",
    helpText:
      "Yes means outcomes above the upper bound or nominal maximum are still possible. No means higher outcomes are impossible by definition.",
    formatValue: formatBooleanValue,
  },
}

function formatBooleanValue(value) {
  return value ? "Yes" : "No"
}

function humanizeFieldName(fieldName) {
  return fieldName
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

function questionType(question) {
  return question?.question_type || question?.type || "draft"
}

function hasRenderableFieldValue(value) {
  if (value === null || value === undefined) return false
  if (typeof value === "string") return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  return true
}

function formatFieldValue(value) {
  if (Array.isArray(value)) return value.join(", ")
  if (typeof value === "boolean") return formatBooleanValue(value)
  return String(value)
}

function getAdditionalFields(question) {
  if (!question || typeof question !== "object") return []

  const visibleKeys = Object.keys(question).filter((key) => {
    if (key === "type" || key === "question_type" || PRIMARY_QUESTION_FIELDS.has(key) || HIDDEN_QUESTION_FIELDS.has(key)) return false
    return hasRenderableFieldValue(question[key])
  })

  const orderedKeys = ADDITIONAL_QUESTION_FIELD_ORDER[questionType(question)] || []
  const keys = [...orderedKeys.filter((key) => visibleKeys.includes(key)), ...visibleKeys.filter((key) => !orderedKeys.includes(key))]

  return keys.map((key) => [key, question[key]])
}

function InfoTooltip({ text }) {
  return (
    <Tooltip label={text} multiline w={260}>
      <ThemeIcon variant="light" size="sm" radius="xl" style={{ cursor: "help" }}>
        i
      </ThemeIcon>
    </Tooltip>
  )
}

function AdditionalFieldRow({ fieldName, value }) {
  const definition = QUESTION_FIELD_DEFINITIONS[fieldName] || {}
  const label = definition.label || humanizeFieldName(fieldName)
  const helpText = definition.helpText || "Additional structured setting used to define how this question works."
  const formattedValue = definition.formatValue ? definition.formatValue(value) : formatFieldValue(value)

  return (
    <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
      <Group gap="xs" wrap="nowrap" align="center">
        <Text fw={600} size="sm">
          {label}
        </Text>
        <InfoTooltip text={helpText} />
      </Group>
      <Badge variant="light" size="lg" radius="sm" style={{ whiteSpace: "normal", textAlign: "left" }}>
        {formattedValue}
      </Badge>
    </Group>
  )
}

function AdditionalFieldsSection({ question }) {
  const fields = getAdditionalFields(question)

  return (
    <Accordion variant="contained" radius="md">
      <Accordion.Item value="additional-fields">
        <Accordion.Control>Additional fields</Accordion.Control>
        <Accordion.Panel>
          <Stack gap="sm">
            {fields.length > 0 ? (
              fields.map(([fieldName, value]) => (
                <AdditionalFieldRow key={fieldName} fieldName={fieldName} value={value} />
              ))
            ) : (
              <Text size="sm" c="dimmed">
                No additional structured fields for this question type.
              </Text>
            )}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  )
}

function ImportedQuestionNotice({ question }) {
  const sourceName = question?.external_source?.name || "the original source"
  const sourceUrl = question?.external_source?.url

  return (
    <Text c="dimmed">
      This question was imported from an outside source. Additional details about this question can be found on{" "}
      {sourceUrl ? (
        <Anchor href={sourceUrl} target="_blank" rel="noreferrer noopener">
          {sourceName}
        </Anchor>
      ) : (
        sourceName
      )}
      .
    </Text>
  )
}

function isKalshiImportedQuestion(question) {
  const source = question?.external_source
  if (!source || typeof source !== "object") return false

  return source.kind === "kalshi" || source.name === "Kalshi"
}

function KalshiImportedQuestionDisclaimer() {
  return (
    <Alert color="yellow" title="Important" variant="light">
      <Text size="sm">
      Atlas does not provide legal, financial, investment, tax, accounting, medical, employment, compliance, or other professional advice. You should not treat any Atlas output as a recommendation to take or avoid any action, and you should not rely on the Service as your sole source of information for decisions with financial, legal, personal, or operational consequences. You are solely responsible for any decisions you make based on the Service. Atlas does not act as a fiduciary, advisor, or agent for you.
      <br /><br />
      Atlas does not use information from the forecasts you generate to trade on Kalshi.  
      </Text>
    </Alert>
  )
}

export function QuestionDetails({ question, showTitle = true }) {
  if (!question) return null

  const detailsRedacted = !!question.details_redacted
  const showKalshiDisclaimer = detailsRedacted && isKalshiImportedQuestion(question)

  return (
    <Stack gap="md" className="af-question-details">
      {showTitle && question.title ? (
        <Stack gap={4}>
          <Title order={4}>Title</Title>
          <Text style={{ whiteSpace: "pre-wrap" }}>{question.title}</Text>
        </Stack>
      ) : null}

      {detailsRedacted ? <ImportedQuestionNotice question={question} /> : null}
      {showKalshiDisclaimer ? <KalshiImportedQuestionDisclaimer /> : null}

      {!detailsRedacted && question.description ? (
        <Stack gap={4}>
          <Title order={4}>Description</Title>
          <Text c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
            {question.description}
          </Text>
        </Stack>
      ) : null}

      {!detailsRedacted && question.resolution_criteria ? (
        <Stack gap={4}>
          <Title order={4}>Resolution criteria</Title>
          <Text c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
            {question.resolution_criteria}
          </Text>
        </Stack>
      ) : null}

      {!detailsRedacted && question.fine_print ? (
        <Stack gap={4}>
          <Title order={4}>Fine print</Title>
          <Text c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
            {question.fine_print}
          </Text>
        </Stack>
      ) : null}

      {!detailsRedacted && Array.isArray(question.options) && question.options.length > 0 ? (
        <Stack gap={4}>
          <Title order={4}>Options</Title>
          <List className="af-question-option-list" spacing="xs">
            {question.options.map((option, index) => (
              <List.Item key={index}>
                <Text c="dimmed">{option}</Text>
              </List.Item>
            ))}
          </List>
        </Stack>
      ) : null}

      {!detailsRedacted ? <AdditionalFieldsSection question={question} /> : null}
    </Stack>
  )
}
